import { Component, OnInit, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { FooterComponent } from '../../../../shared/components/footer/footer.component';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../../../shared/components/asset-header/asset-header.component';
import { DiscussionSidebarComponent } from '../../discussion-sidebar/discussion-sidebar.component';
import { DiscussionListComponent, DiscussionItem } from '../discussion-list/discussion-list.component';
import { DiscussionApiService } from '@angular-workspace/services';
import { KeycloakService } from 'keycloak-angular';
import { LoginModalComponent } from '../../../../shared/components/login-modal/login-modal.component';
import { ToasterService } from '../../../../services/toaster.service';

@Component({
  selector: 'app-discussion-page-base',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    DiscussionSidebarComponent,
    DiscussionListComponent,
    LoginModalComponent
  ],
  templateUrl: './discussion-page-base.component.html',
  styleUrl: './discussion-page-base.component.scss',
})
export class DiscussionPageBaseComponent implements OnInit {
  @Input() pageTitle: string = '';
  @Input() pageDescription: string = '';
  @Input() activeTab: string = '';
  @Input() categoryType?: string; // For API filtering

  protected router = inject(Router);
  protected route = inject(ActivatedRoute);
  protected discussionApiService = inject(DiscussionApiService);
  protected keycloakService = inject(KeycloakService);
  protected toasterService = inject(ToasterService);

  discussions: DiscussionItem[] = [];
  isLoggedIn: boolean = false;
  showLoginModal: boolean = false;

  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Discussions', route: '/discussions' }
    ],
    title: 'Discussions',
    description: 'Join conversations, share insights, and collaborate with the community'
  };

  ngOnInit(): void {
    this.checkLoginStatus();
    this.fetchDiscussions();
  }

  async checkLoginStatus(): Promise<void> {
    try {
      this.isLoggedIn = await this.keycloakService.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      this.isLoggedIn = false;
    }
  }

  async fetchDiscussions(): Promise<void> {
    try {
      const filters: any = {};
      if (this.categoryType) {
        filters.type = [this.categoryType];
      }

      const response = await this.discussionApiService.getDiscussionChoices({
        filters,
        sort_by: 'newest',
        page: 1,
        limit: 10,
        pinned: true
      });

      // Extract discussions array (API returns data.discussions, not data as array)
      const rawList = this.getDiscussionsListFromResponse(response);
      this.discussions = rawList.map((item: any) => this.transformToDiscussionItem(item));
    } catch (error) {
      console.error('Error fetching discussions:', error);
      this.discussions = [];
    }
  }

  /** Extract discussions array from API response (handles data.discussions, data, etc.) */
  private getDiscussionsListFromResponse(response: any): any[] {
    if (!response) return [];
    const data = response.data ?? response;
    if (Array.isArray(data)) return data;
    if (data?.discussions && Array.isArray(data.discussions)) return data.discussions;
    if (data?.discussion && Array.isArray(data.discussion)) return data.discussion;
    return [];
  }

  transformToDiscussionItem(item: any): DiscussionItem {
    return {
      id: item.id || item._id || '',
      title: item.title || 'Untitled Discussion',
      description: item.description || item.content || '',
      author: item.user?.name || item.author?.name || item.author || item.created_by || 'Unknown',
      category: item.category || item.type || '',
      replies: item.reply_count ?? item.replies ?? item.comment_count ?? 0,
      views: item.view_count ?? item.views ?? 0,
      lastActivity: item.updated_at ?? item.updatedAt ?? item.last_activity ?? item.created_at ?? item.createdAt ?? new Date().toISOString(),
      isPinned: item.is_pinned ?? item.pinned ?? false,
      isBookmarked: item.is_bookmarked ?? item.bookmarked ?? false,
      tags: item.tags || [],
      attachments: item.attachments || []
    };
  }

  createNewDiscussion(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    const tabToCategoryMap: { [key: string]: string } = {
      'getting-started': 'GETTING_STARTED',
      'public': 'PUBLIC',
      'general': 'GENERAL',
      'feedback': 'PRODUCT_FEEDBACK',
      'announcement': 'PRODUCT_ANNOUNCEMENTS'
    };

    const category = tabToCategoryMap[this.activeTab] || '';
    const queryParams: any = {};
    if (category) {
      queryParams.category = category;
    }
    if (this.activeTab) {
      queryParams.tab = this.activeTab;
    }

    this.router.navigate(['/discussions/create-new'], { queryParams });
  }

  async onBookmarkClick(event: { event: Event; discussion: DiscussionItem }): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    const newBookmarkState = !event.discussion.isBookmarked;
    const previousState = event.discussion.isBookmarked;
    event.discussion.isBookmarked = newBookmarkState;
    try {
      if (previousState) {
        await this.discussionApiService.unbookmarkDiscussion(event.discussion.id);
      } else {
        await this.discussionApiService.bookmarkDiscussion(event.discussion.id);
      }
      this.toasterService.success(
        newBookmarkState
          ? 'This discussion has been added to your bookmarks. You can open it anytime from My Bookmarks.'
          : 'This discussion has been removed from your bookmarks.'
      );
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      event.discussion.isBookmarked = previousState;
      this.toasterService.error((error as any)?.error?.detail || 'Failed to update bookmark');
    }
  }

  async onPinClick(event: { event: Event; discussion: DiscussionItem }): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    const newPinState = !event.discussion.isPinned;
    const previousState = event.discussion.isPinned;
    event.discussion.isPinned = newPinState;
    try {
      if (previousState) {
        await this.discussionApiService.unpinDiscussion(event.discussion.id);
      } else {
        await this.discussionApiService.pinDiscussion(event.discussion.id);
      }
      this.toasterService.success(
        newPinState
          ? 'This discussion is now pinned. Find it under My Pinned in Discussions.'
          : 'This discussion has been unpinned.'
      );
    } catch (error) {
      console.error('Error toggling pin:', error);
      event.discussion.isPinned = previousState;
      this.toasterService.error((error as any)?.error?.detail || 'Failed to update pin');
    }
  }
}
